import { createContext, useCallback, useContext, useState } from 'react';
import styles from './ToastViewport.module.css';

const ToastContext = createContext(null);

let toastId = 0;

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    const dismiss = useCallback((id) => {
        setToasts((list) => list.filter((t) => t.id !== id));
    }, []);

    const showToast = useCallback(
        ({ message, variant = 'info', duration = 4800 }) => {
            const id = ++toastId;
            setToasts((list) => [...list, { id, message, variant }]);
            window.setTimeout(() => dismiss(id), duration);
        },
        [dismiss],
    );

    const toast = useCallback(
        (message, variant = 'info', duration) => {
            showToast({ message, variant, duration });
        },
        [showToast],
    );

    const value = {
        showToast,
        toast: {
            success: (msg, duration) => toast(msg, 'success', duration),
            error: (msg, duration) => toast(msg, 'error', duration),
            info: (msg, duration) => toast(msg, 'info', duration),
        },
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div className={styles.viewport} aria-live="polite" aria-relevant="additions text">
                {toasts.map((t) => (
                    <div
                        key={t.id}
                        className={`${styles.toast} ${styles[`variant_${t.variant}`]}`}
                        role="status"
                    >
                        <span className={styles.icon} aria-hidden>
                            {t.variant === 'success' ? '✓' : t.variant === 'error' ? '!' : 'i'}
                        </span>
                        <p className={styles.message}>{t.message}</p>
                        <button
                            type="button"
                            className={styles.dismiss}
                            onClick={() => dismiss(t.id)}
                            aria-label="Dismiss notification"
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        return {
            showToast: () => {},
            toast: { success: () => {}, error: () => {}, info: () => {} },
        };
    }
    return ctx;
}
